/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import {
    func, string, shape, array,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@material-ui/core';
import ReactHtmlParser from 'react-html-parser';
import { makeStyles } from '@material-ui/styles';

import configENV from '../../../config';
import { isEmpty } from '../../../utils/object';
import CustomizedCheckbox from '../Partials/CustomCheckbox';
import CustomizedRadio from '../Partials/CustomRadioButton';
import LinearProgressBar from '../Partials/LinearProgressBar';
import EventDispatcher from '../../../state/reducers/EventDispatcher';

const { PERSIST_KEY } = configENV;

const useStyles = makeStyles((theme) => ({
    container: {
        height: '100%',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
    },
    btnContainer: {
        marginTop: '40px',
        display: 'flex',
        textAlign: 'center',
        justifyContent: 'space-evenly',
    },
    primaryButtonStyle: {
        width: '40%',
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '5px 0',
        borderRadius: '30px',
        color: theme.palette.white,
        backgroundColor: theme.palette.colorPrimary,
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.colorPrimary,
        },
        '@media screen and (max-width: 400px)': {
            fontSize: '12px',
        },
    },
    secondaryButtonStyle: {
        width: '40%',
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '5px 0',
        boxShadow: 'none',
        borderRadius: '30px',
        color: theme.palette.black,
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.black}`,
        '&:hover, &:focus, &:active': {
            backgroundColor: theme.palette.grey8,
        },
        '@media screen and (max-width: 400px)': {
            fontSize: '12px',
        },
    },
    progressBarContainer: {
        width: '80%',
        margin: '30px auto 5px',
        textAlign: 'center',
    },
    headerContent: {
        marginBottom: '30px',
    },
    qaContainer: {
        fontSize: '14px',
        '& .MuiFormControlLabel-root': {
            margin: '0',
        },
        '& #question': {
            marginBottom: '10px',
        },
        '& #answer': {
            display: 'flex',
            alignItems: 'start',
            padding: '2px 0',
            '& .MuiCheckbox-root, & .MuiButtonBase-root': {
                padding: '3px 8px !important',
            },
            '& input': {
                width: '100%',
                border: 'none',
                outline: 'none',
                marginLeft: '10px',
                background: 'transparent',
                borderBottom: '1px solid black !important',
                '&:-webkit-autofill, &:-webkit-autofill:hover, &:-webkit-autofill:focus': {  // setting background color to white while autofilling
                    '-webkit-box-shadow': '0 0 0 30px white inset !important',
                },
            },
        },
        '& #max-label': {
            fontSize: '10px',
            marginLeft: '30px',
            color: theme.palette.grey4,
        },
    },
    successCopy: {
        fontSize: '14px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    disabled: {
        cursor: 'no-drop',
    },
    others: {
        width: '100%',
    },
}));

function CancellationSurvey({
    subscription, handleCancel, data,
}) {
    const maxCharacters = 150;
    const totalQuestions = data?.question_answer?.length || 0;
    const initialRatio = totalQuestions === 0 ? 0 : (100 / totalQuestions);
    const markOther = '$$~Other~$$'; // adding special character to distinguish the other fields answer in the answers array
    const copy = data?.copy || 'We are sorry to  see you go and are interested in why you decided to cancel your subscription. Your responses are confidential and will not be shared with any other.';

    const classes = useStyles();
    const dispatch = useDispatch();
    const [count, setCount] = useState(1);
    const [ratio, setRatio] = useState(initialRatio);
    const displaySuccess = count === totalQuestions + 1; // to display the success window

    const userProfile = useSelector((state) => state[PERSIST_KEY]?.userProfile || state.userProfile);

    // saving user response of all the questions
    const [response, setResponse] = useState([]);
    let enableContinueBtn = false;

    if (!isEmpty(response[count - 1]) && response[count - 1].answers.some((x) => x.selected)) {
        enableContinueBtn = true;
        const othersIndex = response[count - 1].answers.findIndex((x) => x.answer.includes(markOther));
        if (othersIndex >= 0 && response[count - 1].answers[othersIndex].selected) {
            const othersInput = response[count - 1]?.answers[othersIndex]?.answer?.replace(markOther, '');
            if (othersInput.replace(/\s+/g, '').length !== 0) enableContinueBtn = true;
            else enableContinueBtn = false;
        }
    }

    useEffect(() => {
        if (displaySuccess) {
            // removing the special keyword ($$~Other~$$) from response
            let quesAnsList = JSON.stringify(response).replaceAll(markOther, '');
            quesAnsList = JSON.parse(quesAnsList);
            dispatch(EventDispatcher.cancellationSurvey(userProfile.email, subscription.ruleId, subscription.productId, quesAnsList));
            setTimeout(() => {
                handleCancel('cancellation_survey_completed', subscription.ruleId, 'Cancellation Survey Completed - Cancel Subscription');
            }, 2000);
        }
    }, [displaySuccess]);

    useEffect(() => {
        if (!isEmpty(data?.question_answer)) {
            const res = [];
            data.question_answer.forEach((ques, i) => {
                const { question, answers, enable_others } = ques;
                res.push({
                    question,
                    answers: answers.map((ans) => ({ answer: ans.answer, selected: false })),
                });
                if (enable_others) {
                    res[i].answers.push({ answer: `${markOther}`, selected: false });
                }
            });
            setResponse(res);
        }
    }, [data?.question_answer]);

    const handleCheck = (e, qNum, ansIndex, isRadio = false) => {
        const res = [...response];
        if (isRadio) {
            res[qNum - 1].answers.forEach((x, i) => {
                res[qNum - 1].answers[i].selected = false;
            });
        }
        res[qNum - 1].answers[ansIndex].selected = e.target.checked;
        setResponse(res);
    };

    const handleOtherAns = (e, qNum, ansIndex) => {
        const res = [...response];

        // preventing users to enter more than max character limit and to add unwanted spaces before text
        if ((e?.target?.value?.length > 0 && e?.target?.value?.replace(/\s+/g, '').length === 0) || e?.target?.value?.length > maxCharacters) return;
        res[qNum - 1].answers[ansIndex].answer = markOther + e?.target.value;
        setResponse(res);
    };

    const handleContinue = () => {
        if (!enableContinueBtn) return;
        const newCount = count + 1;
        setCount(newCount);
        setRatio(initialRatio * newCount);
    };

    const handleBack = () => {
        const newCount = count - 1;
        setCount(newCount);
        setRatio(initialRatio * newCount);
    };

    const others = (qNum, ansIndex, ans) => {
        const answer = ans?.answer?.replace(markOther, '');
        return (
            <div className={classes.others} id="answer">
                Other <input value={answer} className={!ans.selected ? classes.disabled : ''} disabled={!ans.selected} name="other" onChange={(e) => handleOtherAns(e, qNum, ansIndex)} />
            </div>
        );
    };

    const renderQuestion = (qa, qNum) => {
        const { question, answers } = qa;
        const { answer_type } = data?.question_answer[qNum - 1];
        return (
            <div className={classes.qaContainer}>
                <div id="question">Question: {qNum} &nbsp; {question}</div>
                {answers?.map((ans, i) => {
                    const { answer, selected } = ans;
                    const isOthers = answer.includes(markOther);
                    const isRadioBtn = answer_type === 'radio';
                    return (
                        <>
                            <div id="answer">
                                {isRadioBtn ? <CustomizedRadio checked={selected} onChange={(e) => handleCheck(e, qNum, i, true)} />
                                    : <CustomizedCheckbox checked={selected} onChange={(e) => handleCheck(e, qNum, i)} />}
                                {isOthers ? others(qNum, i, ans) : answer}
                            </div>
                            {isOthers && <div id="max-label">{maxCharacters - answer.replace(markOther, '').length} characters left.</div>}
                        </>
                    );
                })}
            </div>
        );
    };

    const renderSuccess = () => (
        <div className={classes.successCopy}>{ReactHtmlParser(data?.success_copy)}</div>
    );

    const renderQuestionAnswers = () => (
        <>
            <div className={classes.contentContainer}>
                {response?.map((qa, i) => (
                    <>
                        {i === count - 1 && (
                            <>
                                {i === 0 && (
                                    <div className={classes.headerContent}>
                                        {copy}
                                    </div>
                                )}
                                {renderQuestion(qa, count)}
                            </>
                        )}
                    </>
                ))}
            </div>
            <div>
                <div className={classes.btnContainer}>
                    {count > 1 && <Button onClick={handleBack} className={classes.secondaryButtonStyle}>Back</Button>}
                    <Button onClick={handleContinue} className={`${classes.primaryButtonStyle} ${!enableContinueBtn ? classes.disabled : ''}`}>Continue</Button>
                </div>
                <div className={classes.progressBarContainer}>
                    <LinearProgressBar value={ratio} />
                    <div>{count}/{totalQuestions}</div>
                </div>
            </div>
        </>
    );

    return (
        <div className={classes.container}>
            {displaySuccess ? renderSuccess() : renderQuestionAnswers()}
        </div>
    );
}

CancellationSurvey.propTypes = {
    subscription: shape({
        productId: string,
    }),
    handleCancel: func.isRequired,
    data: shape({
        copy: string,
        question_answer: array.isRequired,
    }).isRequired,
};

CancellationSurvey.defaultProps = {
    subscription: {},
};

export default CancellationSurvey;
